/**
 * Sync v2 — diferencial por registro, E2E cifrado con clave de cuenta.
 * Contrato: docs/contracts/embudo-v1.md. El Worker no cambia.
 *
 * Push: registros sucios (no se compara reloj del dispositivo con reloj del servidor).
 * Pull: se aplica un remoto solo si su updatedAt es >= al local (LWW).
 */

import type { SyncConfig, SyncRecord } from './types';
import { encryptV2, decryptAny, LegacyPayloadError } from './crypto';
import {
  getAllRecords,
  getRecord,
  getDirtyIds,
  clearDirty,
  markAllDirty,
  putLocal,
  putRemoteIfNewer,
  getLastSyncAt,
  setLastSyncAt
} from './db';

export interface SyncEnvelope {
  id: string;
  updatedAt: number;
  deleted: boolean;
  payload: string;
}

export interface SyncKeys {
  key: CryptoKey;
  /** Solo hace falta para leer payloads v1 heredados. */
  password?: string;
}

export interface SyncResult {
  pushed: number;
  pulled: number;
  legacyPending: number;
  /** Payloads que no se pudieron descifrar (clave distinta, datos corruptos). No cuenta v1 sin password. */
  failed: number;
  serverTime: number;
}

export type SyncStatus = 'idle' | 'syncing' | 'ok' | 'error' | 'no-config' | 'locked';

const BATCH = 500; // MAX_ITEMS_PER_REQUEST del Worker

async function envelope(rec: SyncRecord, key: CryptoKey): Promise<SyncEnvelope> {
  const { id, updatedAt, deleted, ...rest } = rec;
  return { id, updatedAt, deleted, payload: await encryptV2(rest, key) };
}

async function post(config: SyncConfig, since: number, items: SyncEnvelope[]) {
  const resp = await fetch(config.workerUrl + '/sync', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ code: config.code, deviceId: config.deviceId, since, items })
  });
  if (!resp.ok) throw new Error(`Sync HTTP ${resp.status}: ${await resp.text()}`);
  return (await resp.json()) as { serverTime: number; items: SyncEnvelope[] };
}

export async function sync(config: SyncConfig, keys: SyncKeys): Promise<SyncResult> {
  if (!config.workerUrl) throw new Error('Worker URL no configurada');
  if (!config.code) throw new Error('Sync code no configurado');

  const since = await getLastSyncAt();
  const dirtyIds = await getDirtyIds();
  const dirty: SyncRecord[] = [];
  for (const id of dirtyIds) {
    const r = await getRecord(id);
    if (r) dirty.push(r);
  }

  let pushed = 0;
  let pulled = 0;
  let legacyPending = 0;
  let failed = 0;
  let serverTime = since;
  const chunks: SyncRecord[][] = [];
  for (let i = 0; i < dirty.length; i += BATCH) chunks.push(dirty.slice(i, i + BATCH));
  if (chunks.length === 0) chunks.push([]);

  for (const chunk of chunks) {
    const wrapped = await Promise.all(chunk.map((r) => envelope(r, keys.key)));
    const data = await post(config, since, wrapped);
    serverTime = Math.max(serverTime, data.serverTime);
    pushed += wrapped.length;
    for (const env of data.items) {
      try {
        const rest = await decryptAny<Record<string, unknown>>(env.payload, keys.key, keys.password);
        const rec = { ...rest, id: env.id, updatedAt: env.updatedAt, deleted: env.deleted } as SyncRecord;
        if (await putRemoteIfNewer(rec)) pulled++;
      } catch (e) {
        if (e instanceof LegacyPayloadError) legacyPending++;
        else {
          failed++;
          console.warn('No se pudo descifrar', env.id, e);
        }
      }
    }
    await clearDirty(chunk.map((r) => ({ id: r.id, updatedAt: r.updatedAt })));
  }

  // Si quedaron payloads sin leer (v1 sin password o clave distinta), no se avanza el cursor:
  // vuelven en el próximo sync.
  if (legacyPending === 0 && failed === 0) await setLastSyncAt(serverTime);
  return { pushed, pulled, legacyPending, failed, serverTime };
}

export async function fullPull(config: SyncConfig, keys: SyncKeys): Promise<SyncResult> {
  await setLastSyncAt(0);
  return sync(config, keys);
}

/**
 * Migración a v2: baja todo (leyendo v1 con password), sube todo re-cifrado.
 * updatedAt se renueva porque el Worker solo pisa filas con updatedAt mayor.
 */
export async function migrateToV2(
  config: SyncConfig,
  keys: Required<SyncKeys>
): Promise<SyncResult> {
  const first = await fullPull(config, keys);
  if (first.legacyPending > 0) throw new Error('Quedaron registros v1 sin descifrar: revisá la password');
  if (first.failed > 0) throw new Error(`${first.failed} registros no se pudieron descifrar: revisá password y código`);
  const now = Date.now();
  for (const { id } of await getAllRecords()) {
    // Re-lectura justo antes de renovar: achica la ventana de pisar una escritura concurrente.
    const r = await getRecord(id);
    if (r) await putLocal({ ...r, updatedAt: now });
  }
  await markAllDirty();
  return sync(config, keys);
}
